import React from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';

import ICON_COMPLETE from 'assets/slickpay-splash/icon-complete.svg';
import ICON_ORDER from 'assets/slickpay-splash/icon-order.svg';
import Button from 'components/form/Button.component';
import Badge from 'components/generic/Badge.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import StripeAccountVerifierModal from 'components/modal/StripeAccountVerifierModal.component';
import LINK from 'constants/link.const';
import STRIPE_STATUS from 'constants/pos/stripeStatus.const';
import S from 'constants/string.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useIsSalonRestrictedToCriticalServices } from 'providers/SalonBasicInfo.provider';
import { selectCurrentSalonId, selectCurrentSalonStripeStatus } from 'selectors/currentSalon.selectors';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';

import CN from './StatusBanner.module.scss';

const CTAG = 'ReaderStatusBanners';

const L = logger(CTAG);
const ROADMAP_LINK_INCOMPLETE = 'https://help.getslick.com/en/articles/4178961-slickpay-documents-check-list';

const ReaderStatusBanners = ({ status, salonId, onVerifyClick }) => {
  const { t } = useTranslation();
  const isRestrictedToCriticalServices = useIsSalonRestrictedToCriticalServices();

  L.debug('()', 'status:', status);

  // Complete, Enabled, Pending, Restricted, Incomplete, No account
  // Note: If status is “enabled” show the “Pending” design state.

  const isComplete = status === STRIPE_STATUS.complete && !isRestrictedToCriticalServices;
  const isUnverified = status === STRIPE_STATUS.complete && isRestrictedToCriticalServices;
  const isIncomplete = status === STRIPE_STATUS.incomplete || status === STRIPE_STATUS.noAccount;
  const isInProgress = status === STRIPE_STATUS.pending || status === STRIPE_STATUS.enabled;
  const isRestricted = status === STRIPE_STATUS.restricted;

  if (isInProgress) {
    return (
      <div data-bem="StatusBanner__pending" className={cn(CN.container, CN.pending)}>
        <span className={CN.bannerNumber}>{t('slickPay.ordered.readerBanner.bannerNumber')}</span>
        <NavLink
          to={linkTo({
            pattern: LINK.salonOnline,
            params: { salonId },
            fault: '#',
          })}
        >
          <div className={CN.text}>
            <img src={ICON_COMPLETE} alt="Icon status pending" className={CN.icon} />
            <span className={CN.statusTitle}>{t('slickPay.ordered.readerBanner.statusTitle')}</span>

            <div className={CN.badgeContainer}>
              <Badge variant="attention">
                <div className={CN.buttonInner}>
                  <Icon variant="xCircleSolid" width="md" />
                  <span className={CN.withIcon}>{t('slickPay.ordered.readerBanner.pendingBadge')}</span>
                </div>
              </Badge>
            </div>
            <span className={CN.description}>
              <span className={CN.purple}>{t('slickPay.ordered.readerBanner.pendingDescriptionPart1')}</span>
              {t('slickPay.ordered.readerBanner.pendingDescriptionPart2')}
            </span>
          </div>
        </NavLink>
      </div>
    );
  }
  if (isComplete) {
    return (
      <div data-bem="StatusBanner_complete" className={cn(CN.container, CN.complete)}>
        <span className={CN.bannerNumber}>{t('slickPay.ordered.readerBanner.bannerNumber')}</span>
        <NavLink
          to={linkTo({
            pattern: LINK.salonOnline,
            params: { salonId },
            fault: '#',
          })}
        >
          <div className={CN.text}>
            <img src={ICON_COMPLETE} alt="Icon status complete" className={CN.icon} />
            <span className={CN.statusTitle}>{t('slickPay.ordered.readerBanner.statusTitle')}</span>

            <div className={CN.badgeContainer}>
              <Badge variant="success" invertedDark>
                <div className={CN.buttonInner}>
                  <Icon variant="checkCircleSolid" width="md" />
                  <span className={CN.withIcon}>{t('slickPay.ordered.readerBanner.completeBadge')}</span>
                </div>
              </Badge>
            </div>
            <span className={CN.description}>
              <span className={CN.purple}>{t('slickPay.ordered.readerBanner.completeDescriptionPart1')}</span>
              {S.space} {t('slickPay.ordered.readerBanner.completeDescriptionPart2')}
            </span>
          </div>
        </NavLink>
      </div>
    );
  }

  if (isUnverified) {
    return (
      <div data-bem="StatusBanner_unverified" className={cn(CN.container, CN.restricted)}>
        <span className={CN.bannerNumber}>{t('slickPay.ordered.readerBanner.bannerNumber')}</span>

        <div className={CN.text}>
          <img src={ICON_COMPLETE} alt="Icon status unverified" className={CN.icon} />
          <span className={CN.statusTitle}>{t('slickPay.ordered.readerBanner.statusTitle')}</span>

          <div className={CN.badgeContainer}>
            <Badge variant="restricted">
              <div className={CN.buttonInner}>
                <Icon variant="xCircleSolid" width="md" />
                <span className={CN.withIcon}>{t('slickPay.ordered.readerBanner.incompleteBadge')}</span>
              </div>
            </Badge>
          </div>
          <span className={CN.description}>{t('slickPay.ordered.readerBanner.incompleteDescriptionPart1')}</span>
          <Button tag="StatusBanner__verify-button" onClick={onVerifyClick} variant="primaryForce" xSpace>
            {t('slickPay.ordered.readerBanner.verifyIdentityButton')}
          </Button>
        </div>
      </div>
    );
  }
  if (isRestricted) {
    return (
      <div data-bem="StatusBanner_restricted" className={cn(CN.container, CN.restricted)}>
        <span className={CN.bannerNumber}>{t('slickPay.ordered.readerBanner.bannerNumber')}</span>
        <NavLink
          to={linkTo({
            pattern: LINK.salonOnline,
            params: { salonId },
            fault: '#',
          })}
        >
          <div className={CN.text}>
            <img src={ICON_COMPLETE} alt="Icon status restricted" className={CN.icon} />
            <span className={CN.statusTitle}>{t('slickPay.ordered.readerBanner.statusTitle')}</span>

            <div className={CN.badgeContainer}>
              <Badge variant="restricted">
                <div className={CN.buttonInner}>
                  <Icon variant="xCircleSolid" width="md" />
                  <span className={CN.withIcon}>{t('slickPay.ordered.readerBanner.restrictedBadge')}</span>
                </div>
              </Badge>
            </div>
            <span className={CN.description}>
              <span className={CN.purple}>{t('slickPay.ordered.readerBanner.completeDescriptionPart1')}</span>
              {S.space} {t('slickPay.ordered.readerBanner.completeDescriptionPart2')}
            </span>
          </div>
        </NavLink>
      </div>
    );
  }
  if (isIncomplete) {
    return (
      <div data-bem="StatusBanner__Incomplete" className={cn(CN.container, CN.incomplete)}>
        <span className={CN.bannerNumber}>{t('slickPay.ordered.readerBanner.bannerNumber')}</span>

        <div className={CN.text}>
          <img src={ICON_COMPLETE} alt="Icon status incomplete" className={CN.icon} />
          <span className={CN.statusTitle}>{t('slickPay.ordered.readerBanner.statusTitle')}</span>

          <div className={CN.badgeContainer}>
            <Badge variant="restricted">
              <div className={CN.buttonInner}>
                <Icon variant="xCircleSolid" width="md" />
                <span className={CN.withIcon}>{t('slickPay.ordered.readerBanner.incompleteBadge')}</span>
              </div>
            </Badge>
          </div>
          <span className={CN.description}>{t('slickPay.ordered.readerBanner.incompleteDescriptionPart1')}</span>

          <ExternalLink link={ROADMAP_LINK_INCOMPLETE} variant="buttonPrimary" xSpace>
            {t('slickPay.ordered.readerBanner.incompleteDescriptionPart2')}
          </ExternalLink>
        </div>
      </div>
    );
  }
  return <div />;
};

const StatusBanner = ({ salonId, status }) => {
  const { t } = useTranslation();
  const { val: restrictedModal, yay: showRestrictedModal, nay: hideRestrictedModal } = useTip(false);

  return (
    <section data-bem="StatusBanner" className={CN.wrapper}>
      <span className={CN.blur1} data-bem="StatusBanner__blur1" />
      <div data-bem="StatusBanner_ordered" className={cn(CN.container, CN.ordered)}>
        <span className={CN.bannerNumber}>{t('slickPay.ordered.statusBanner.bannerNumber')}</span>
        <div className={CN.text}>
          <img src={ICON_ORDER} alt="Icon status order" className={CN.icon} />
          <span className={CN.statusTitle}>{t('slickPay.ordered.statusBanner.orderStatus')}</span>

          <div className={CN.badgeContainer}>
            <Badge variant="success" invertedDark>
              <div className={CN.buttonInner}>
                <Icon variant="checkCircleSolid" width="md" />
                <span className={CN.withIcon}>{t('slickPay.ordered.statusBanner.badge')}</span>
              </div>
            </Badge>
          </div>
          <span className={CN.description}>
            {t('slickPay.ordered.statusBanner.descriptionPart1', { suffix: S.deliveryTruck })}
            {S.space}
            <span className={cn(CN.purple, CN.nounderline)}>{t('slickPay.ordered.statusBanner.descriptionPart2')}</span>
          </span>
        </div>
      </div>

      <ReaderStatusBanners status={status} salonId={salonId} onVerifyClick={showRestrictedModal} />

      <StripeAccountVerifierModal
        tag="StatusBanner__restriction-modal"
        enabled={restrictedModal}
        onCancel={hideRestrictedModal}
        onClose={hideRestrictedModal}
      />
    </section>
  );
};

const mapStateToProps = (state) => ({
  salonId: selectCurrentSalonId(state),
  status: selectCurrentSalonStripeStatus(state),
});

export default connect(mapStateToProps, null)(StatusBanner);
